import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth.store";
import {
  getNotifications,
  getNotificationSummary,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
} from "@/services/notification.service";

export const notificationsKeys = {
  all: ["notifications"] as const,
  list: (page: number) => ["notifications", "list", page] as const,
  summary: ["notifications", "summary"] as const,
};

// Le backend ferme `/v1/notifications*` derrière la vérification d'e-mail : un compte non vérifié
// (ex. juste après un premier checkout) reçoit un 403 « Your email address is not verified. » à
// chaque appel, remonté en erreur Sentry (SUCCESCHOOL-FRONTEND-V2-8). Comme la messagerie
// (`useMessaging`), on ne lance donc rien tant que l'e-mail n'est pas vérifié ; la modale de
// vérification prend le relais côté UI.
export function useNotificationSummary(enabled = true) {
  const emailVerified = useAuthStore((s) => !!s.user?.email_verified_at);
  return useQuery({
    queryKey: notificationsKeys.summary,
    queryFn: getNotificationSummary,
    staleTime: 60_000,
    // `AppHeader` monte ce hook dans les quatre layouts : un intervalle court y était payé par
    // toute session authentifiée en continu, alors que `useNotificationChannel` invalide déjà
    // cette clé sur `.notification.created`. L'intervalle est conservé comme filet contre une
    // diffusion Echo perdue (compteur figé jusqu'au prochain événement), mais assez long pour
    // ne plus constituer un trafic de fond.
    refetchInterval: 10 * 60_000,
    refetchIntervalInBackground: false,
    enabled: enabled && emailVerified,
  });
}

export function useNotifications(page = 1, enabled = true) {
  const emailVerified = useAuthStore((s) => !!s.user?.email_verified_at);
  return useQuery({
    queryKey: notificationsKeys.list(page),
    queryFn: () => getNotifications(page),
    placeholderData: keepPreviousData,
    enabled: enabled && emailVerified,
  });
}

export function useMarkNotificationAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => markNotificationAsRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}

export function useMarkAllNotificationsAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteNotification(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}
